// Cookie-consent banner (portfolio analytics standard, two-tier).
//
// Basic reach measurement is anonymous + cookie-free and runs WITHOUT consent
// (see src/lib/analytics/posthog.ts) — nothing is stored on the device, so
// §25 TTDSG does not apply. This banner gates ONLY the persistent tier:
//   - Accept  → cookie devotionfund_consent=granted (1yr); PostHog switches to
//               localStorage+cookie (cross-session) + session replay on.
//   - Decline → cookie devotionfund_consent=denied  (1yr); PostHog opts out +
//               resets (wipes any stale on-device state).
// Both buttons are equally weighted (no dark pattern). The banner renders only
// when no decision cookie exists yet, so returning visitors are never nagged.
//
// PRERENDER GUARD: this repo prerenders marketing routes with a headless
// browser that sets window.__PRERENDER_INJECTED = true before app JS runs. We
// never render the banner in that pass, so the static HTML that ships to every
// visitor is banner-free (and gets the correct client-side banner at runtime).

import { useEffect, useRef, useState } from 'react';
import { posthog, CONSENT_COOKIE } from '@/lib/analytics/posthog';

const ONE_YEAR = 60 * 60 * 24 * 365;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name: string, value: string) {
  document.cookie = `${name}=${value}; Max-Age=${ONE_YEAR}; Path=/; SameSite=Lax`;
}

function isPrerender(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window as unknown as { __PRERENDER_INJECTED?: boolean })
      .__PRERENDER_INJECTED === true
  );
}

export default function ConsentBanner() {
  // Start hidden; decide on the client after mount so we never render during
  // SSR/prerender (avoids hydration mismatch) or for a visitor who already
  // chose.
  const [visible, setVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isPrerender()) return; // never show during the prerender pass
    if (readCookie(CONSENT_COOKIE)) return; // already decided
    setVisible(true);
  }, []);

  // Found 2026-10-07: this banner was `fixed bottom-0`, which sat on top of
  // the hero "Support" CTA on mission pages at several viewport heights --
  // a visitor's first tap landed on Decline/Accept instead of opening the
  // donate form. Reserving the banner's height as BOTTOM body padding only
  // protects whatever ends up at the very end of the page's scroll height
  // (e.g. the footer) -- it does nothing for a button positioned earlier in
  // the document that merely happens to land in the unscrolled viewport's
  // bottom N px, which is exactly where the live "Support" button on
  // /mission/tom-neelam sat at 390px/768px (confirmed live 2026-10-08:
  // banner occupied y 656-844, the button's only on-screen portion was
  // 817-844 -- entirely covered, every time, before any scroll).
  // Fixed 2026-10-08 by moving the banner to the TOP of the viewport
  // instead: a bottom-fixed overlay can coincidentally land on top of ANY
  // page's primary CTA depending on that page's content height, but no
  // donate/support button on this site renders in the first ~150px of a
  // page (that's nav/header territory on every route) -- so top placement
  // removes the collision class entirely rather than reshuffling it to a
  // different page. Padding is now reserved on the TOP, pushing content
  // down instead of adding dead space at the end of the document.
  useEffect(() => {
    if (!visible) {
      document.body.style.paddingTop = '';
      return;
    }
    const el = bannerRef.current;
    if (!el) return;
    const update = () => {
      document.body.style.paddingTop = `${el.offsetHeight}px`;
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.body.style.paddingTop = '';
    };
  }, [visible]);

  if (!visible) return null;

  function accept() {
    writeCookie(CONSENT_COOKIE, 'granted');
    try {
      // Upgrade from the default cookieless/anonymous layer to PERSISTENT
      // analytics: on-device storage (cross-session identity + funnel tracing)
      // and session recording on. Before this, everything was in memory.
      posthog.set_config({
        persistence: 'localStorage+cookie',
        disable_persistence: false,
      });
      posthog.opt_in_capturing(); // no-op unless a prior "denied" opted out
      posthog.startSessionRecording?.();
    } catch {
      /* posthog not initialised (no token) — nothing to opt into */
    }
    setVisible(false);
  }

  function decline() {
    writeCookie(CONSENT_COOKIE, 'denied');
    try {
      // Stay opted out + clear any stale on-device PostHog state.
      posthog.opt_out_capturing();
      posthog.reset(true);
    } catch {
      /* no-op */
    }
    setVisible(false);
  }

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 top-0 z-[100] border-b border-slate-200 bg-white/95 px-4 py-3 shadow-[0_2px_12px_rgba(0,0,0,0.08)] backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-slate-600">
          We measure basic, anonymous, cookie-free reach stats (page views and
          visitor counts) — nothing is stored on your device.{' '}
          <span className="font-medium">Accept</span> additionally allows
          cookies for extended analytics (cross-session insights and session
          replay; PostHog, EU-hosted).{' '}
          <span className="font-medium">Decline</span> turns all analytics off.{' '}
          <a
            href="/privacy"
            className="underline hover:text-slate-900"
          >
            Privacy policy
          </a>
        </p>
        <div className="flex flex-none gap-2">
          <button
            type="button"
            onClick={decline}
            className="rounded-md border border-slate-300 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={accept}
            className="rounded-md bg-custom-blue px-4 py-1.5 text-sm font-semibold text-white hover:bg-custom-blue-hover"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
